import { useState, useCallback, useEffect } from 'react'
import Cookies from 'js-cookie'
import { CookieOptions, UseCookieProps } from './useCookie.types'

const parseCookie = <T>(name: string, defaultValue: T): T | null => {
  const cookie = Cookies.get(name)

  if (cookie) {
    try {
      return JSON.parse(cookie) as T
    } catch {
      return cookie as T
    }
  }

  Cookies.set(name, JSON.stringify(defaultValue))

  return defaultValue
}

/**
 * Hook for managing the state of a cookie with support for reading, writing, and deleting.
 *
 * @template T The type of the value stored in the cookie.
 * @param {string} name - The name of the cookie to manage.
 * @param {T} defaultValue - The default value to assign if the cookie does not exist.
 * @returns {{
 *   value: T | null;
 *   updateCookie: (newValue: T, options?: CookieOptions) => void;
 *   deleteCookie: () => void;
 * }} An object containing the current value of the cookie, a function to update the cookie, and another to delete it.
 */
const useCookie = <T>({ name, defaultValue, observeChanges }: UseCookieProps<T>) => {
  const [value, setValue] = useState<T | null>(() => parseCookie(name, defaultValue))

  const updateCookie = useCallback(
    (newValue: T, options?: CookieOptions) => {
      Cookies.set(name, JSON.stringify(newValue), options)
      setValue(newValue)
    },
    [name]
  )

  const deleteCookie = useCallback(() => {
    Cookies.remove(name)
    setValue(null)
  }, [name])

  useEffect(() => {
    if (!observeChanges) return

    const observer = new MutationObserver(
      /* istanbul ignore next */ () => {
        const parsedValue = parseCookie(name, defaultValue)

        if (parsedValue !== value) {
          setValue(parsedValue)
        }
      }
    )

    observer.observe(document, { subtree: true, childList: true, attributes: true })

    return () => observer.disconnect()
  }, [name, value, defaultValue])

  return { value, updateCookie, deleteCookie }
}

export default useCookie
